import { z } from 'zod'

export const EBAY_CHANNEL_SHOP_ID = 1002 as const

/**
 * Accounts that existed before the account registry was introduced.
 *
 * This list is only used to seed/backfill the registry and to preserve the
 * legacy product fallback. It must never be used as the authoritative list of
 * all eBay accounts; that list lives in the `ebayAccounts` collection.
 */
export const EBAY_ACCOUNT_KEYS = ['liebeszuhause', 'homeylove'] as const
const RESERVED_EBAY_ACCOUNT_KEYS = new Set(['__proto__', 'prototype', 'constructor'])

export const ebayAccountKeySchema = z.string()
  .trim()
  .min(2)
  .max(64)
  .regex(
    /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/,
    'eBay account keys may only contain lowercase letters, numbers, hyphens and underscores',
  )
  .refine(
    value => !RESERVED_EBAY_ACCOUNT_KEYS.has(value),
    'This eBay account key is reserved',
  )
export type EbayAccountKey = z.infer<typeof ebayAccountKeySchema>
export type EbayDescriptionMode = 'offer' | 'inventory'

export const EBAY_ENVIRONMENTS = ['sandbox', 'production'] as const
export const ebayEnvironmentSchema = z.enum(EBAY_ENVIRONMENTS)
export type EbayEnvironment = z.infer<typeof ebayEnvironmentSchema>

export const LEGACY_EBAY_ACCOUNT_KEY: EbayAccountKey = 'liebeszuhause'

/**
 * Existing LZH listings can carry eBay's retired Listing Designer. These
 * listings remain on their already-published Offer instead of being recreated
 * through an Inventory description, because the latter is limited to 4,000
 * characters and cannot reproduce the Designer template.
 */
export const EBAY_LISTING_DESIGNER_OFFER_STRATEGY = 'listing-designer-offer-preserving-v1' as const

/**
 * Some inherited offers are still locally offer-backed, but fresh eBay
 * readback proves that their active root is managed through Inventory.  Their
 * Listing Designer presentation remains protected exactly like the original
 * offer-preserving strategy; the distinct value documents the verified remote
 * topology without converting the local description mode.
 */
export const EBAY_LISTING_DESIGNER_INVENTORY_ROOT_STRATEGY =
  'listing-designer-inventory-root-verified-v1' as const

export const EBAY_LISTING_DESIGNER_PRESERVING_STRATEGIES = new Set<string>([
  EBAY_LISTING_DESIGNER_OFFER_STRATEGY,
  EBAY_LISTING_DESIGNER_INVENTORY_ROOT_STRATEGY,
])

export type EbayAccountDefinition = {
  label: string
  shortLabel: string
  locked: boolean
  legacy: boolean
  defaultAccount: boolean
}

/** Static metadata for the two pre-registry accounts only. */
export const EBAY_ACCOUNT_DEFINITIONS: Record<string, EbayAccountDefinition> = {
  liebeszuhause: {
    label: 'eBay Liebes zu Hause',
    shortLabel: 'LZH',
    locked: true,
    legacy: true,
    defaultAccount: true,
  },
  homeylove: {
    label: 'eBay Homeylove',
    shortLabel: 'HYL',
    locked: false,
    legacy: false,
    defaultAccount: false,
  },
}

export const getBuiltinEbayAccountDefinition = (
  accountKey: EbayAccountKey,
): EbayAccountDefinition | undefined => EBAY_ACCOUNT_DEFINITIONS[accountKey]

export const isEbayAccountKey = (value: unknown): value is EbayAccountKey =>
  ebayAccountKeySchema.safeParse(value).success

export const normalizeEbayAccountKeys = (value: unknown): EbayAccountKey[] => {
  const source = Array.isArray(value)
    ? value
    : value && typeof value === 'object' && Array.isArray((value as { ebay?: unknown }).ebay)
      ? (value as { ebay: unknown[] }).ebay
      : []

  return Array.from(new Set(source.filter(isEbayAccountKey)))
}

export const resolveProductEbayAccountKeys = (input: {
  shops?: unknown
  marketplaceAccounts?: unknown
}): EbayAccountKey[] => {
  const shops = Array.isArray(input.shops) ? input.shops : []
  const hasEbayChannel = shops.some(shop => Number(shop) === EBAY_CHANNEL_SHOP_ID)
  if (!hasEbayChannel) return []

  // Mongoose subdocuments expose schema paths through prototype getters.
  // A readable array is explicit even when `ebay` is not an own property;
  // missing/undefined still keeps the legacy fallback, while [] stays empty.
  const hasExplicitAccountSelection = Boolean(
    input.marketplaceAccounts
    && typeof input.marketplaceAccounts === 'object'
    && Array.isArray((input.marketplaceAccounts as { ebay?: unknown }).ebay),
  )
  const selectedAccounts = normalizeEbayAccountKeys(input.marketplaceAccounts)
  return hasExplicitAccountSelection ? selectedAccounts : [LEGACY_EBAY_ACCOUNT_KEY]
}

/**
 * Applies an additive/removal account change and always persists an explicit
 * eBay account array. This is important for bulk operations: shop 1002 without
 * `marketplaceAccounts.ebay` is the historic Liebeszuhause fallback and must
 * never be created accidentally by a new multi-account write path.
 */
export const applyProductEbayAccountAssignmentChange = (input: {
  shops?: unknown
  marketplaceAccounts?: unknown
  add?: unknown
  remove?: unknown
}): {
  shops: number[]
  marketplaceAccounts: Record<string, unknown> & { ebay: EbayAccountKey[] }
} => {
  const shops = Array.from(new Set(
    (Array.isArray(input.shops) ? input.shops : [])
      .map(Number)
      .filter(Number.isFinite),
  ))
  const currentAccounts = resolveProductEbayAccountKeys({
    shops,
    marketplaceAccounts: input.marketplaceAccounts,
  })
  const addAccounts = normalizeEbayAccountKeys(input.add)
  const removeAccounts = new Set(normalizeEbayAccountKeys(input.remove))
  const nextAccounts = Array.from(new Set([
    ...currentAccounts,
    ...addAccounts,
  ])).filter(accountKey => !removeAccounts.has(accountKey))

  const marketplaceAccountsDocument = input.marketplaceAccounts
    && typeof input.marketplaceAccounts === 'object'
    && !Array.isArray(input.marketplaceAccounts)
      ? input.marketplaceAccounts as Record<string, unknown>
      : {}
  // Spreading a Mongoose subdocument copies its internal _doc. When assigned
  // back to a product, Mongoose prefers that stale data over the new ebay array.
  // Convert to plain data first, also retaining any other marketplace fields.
  const marketplaceAccounts = typeof marketplaceAccountsDocument.toObject === 'function'
    ? marketplaceAccountsDocument.toObject()
    : marketplaceAccountsDocument

  return {
    shops: nextAccounts.length > 0
      ? Array.from(new Set([...shops, EBAY_CHANNEL_SHOP_ID]))
      : shops.filter(shopId => shopId !== EBAY_CHANNEL_SHOP_ID),
    marketplaceAccounts: {
      ...marketplaceAccounts,
      ebay: nextAccounts,
    },
  }
}

/**
 * Existing Liebeszuhause listings predate the inventory-only description
 * strategy. Treat only listings with known remote references conservatively
 * as legacy. New listings and every Homeylove listing use inventory text.
 */
export const resolveEbayDescriptionMode = (input: {
  shop: string
  listing?: unknown
}): EbayDescriptionMode => {
  if (input.shop !== LEGACY_EBAY_ACCOUNT_KEY) return 'inventory'

  const listing = input.listing && typeof input.listing === 'object'
    ? input.listing as Record<string, unknown>
    : undefined
  const explicitMode = listing?.descriptionMode
  if (explicitMode === 'offer' || explicitMode === 'inventory') return explicitMode

  const rawOffers = listing?.offers
  const offers = Array.isArray(rawOffers) ? rawOffers : []
  const hasRemoteReferences = offers.length > 0
    || Boolean(String(listing?.listingId ?? '').trim())
    || String(listing?.status ?? '').toUpperCase() === 'PUBLISHED'

  return hasRemoteReferences ? 'offer' : 'inventory'
}

export const isEbayListingDesignerOfferPreserving = (listing: unknown): boolean => {
  const value = listing && typeof listing === 'object'
    ? listing as Record<string, any>
    : undefined
  return value?.descriptionMode === 'offer'
    && EBAY_LISTING_DESIGNER_PRESERVING_STRATEGIES.has(
      String(value?.descriptionMigration?.readback?.strategy ?? ''),
    )
}
